import { useEffect, useState } from 'react';
import { Icon } from '../../atoms/Icon';

interface PlayerWatermarkProps {
  /** ID do conteúdo/aula (contentId do player). */
  contentId?: string;
  /** ID do aluno logado. */
  studentId?: string;
}

function formatDateTime(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function shortId(value?: string) {
  if (!value) return '';
  return value.length > 7 ? `${value.slice(0, 7)}...` : value;
}

/**
 * Marca d'água exibida sobre o player. Contém dois elementos:
 * - Card detalhado no canto superior direito (data/hora do acesso, ID da aula e ID do aluno).
 * - Pílula compacta no canto inferior esquerdo com o ID abreviado do aluno.
 *
 * A exibição por tempo limitado é controlada por quem a renderiza (molécula Player),
 * que a mostra nos primeiros 60s a cada início de reprodução.
 */
export default function PlayerWatermark({
  contentId,
  studentId,
}: PlayerWatermarkProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Card detalhado — canto superior direito */}
      <div
        className='pointer-events-none absolute top-4 right-4 z-[2] select-none rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-[11px] font-medium leading-tight backdrop-blur-sm'
        style={{ color: 'rgba(255, 255, 255, 0.95)' }}
      >
        <div className='grid grid-cols-[16px_auto_1fr] items-center gap-x-2 gap-y-1.5'>
          <Icon
            type='material'
            Icon='calendar_today'
            className='text-[14px] text-white/60'
          />
          <span className='text-white/60'>Data/Hora</span>
          <span className='pl-3 font-mono'>{formatDateTime(now)}</span>

          {contentId && (
            <>
              <Icon
                type='material'
                Icon='smart_display'
                className='text-[14px] text-white/60'
              />
              <span className='text-white/60'>ID Aula</span>
              <span className='pl-3 font-mono'>{contentId}</span>
            </>
          )}

          {studentId && (
            <>
              <Icon
                type='material'
                Icon='person'
                className='text-[14px] text-white/60'
              />
              <span className='text-white/60'>ID Aluno</span>
              <span className='pl-3 font-mono'>{studentId}</span>
            </>
          )}
        </div>
      </div>

      {/* Pílula compacta — canto inferior esquerdo */}
      {studentId && (
        <div
          className='pointer-events-none absolute bottom-4 left-4 z-[2] flex select-none items-center gap-1.5 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[11px] font-medium leading-none backdrop-blur-sm'
          style={{ color: 'rgba(255, 255, 255, 0.9)' }}
        >
          <Icon
            type='material'
            Icon='shield'
            className='text-[14px] text-white/70'
          />
          <span className='font-mono'>Aluno {shortId(studentId)}</span>
        </div>
      )}
    </>
  );
}
